import { useState } from 'react';
import axios from 'axios';
import { X, Store, Loader2, CheckCircle2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { API } from '@/App';

/**
 * Sell Here: an expression of interest, not an application.
 *
 * Vendors are brought on by invitation now, so this asks only what is needed to start a
 * conversation — name, business, email — and the platform emails the two agreements plus the fit
 * questionnaire straight away. A signed-in member only has to add their business name.
 */
export const SellHereModal = ({ open, onClose, currentUser }) => {
  const [form, setForm] = useState({
    full_name: currentUser?.full_name || '',
    business_name: '',
    email: currentUser?.email || '',
    note: '',
  });
  const [state, setState] = useState('idle');
  const [error, setError] = useState('');
  const [done, setDone] = useState('');

  if (!open) return null;

  // Closing resets it, so reopening shows the form rather than the last success screen.
  const close = () => {
    setState('idle');
    setError('');
    setDone('');
    setForm((f) => ({ ...f, business_name: '', note: '' }));
    onClose();
  };

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    setState('sending');
    try {
      const r = await axios.post(`${API}/vendor-interest`, form);
      setDone(r.data.message || 'Thank you — check your email for the next step.');
      setState(r.data.already ? 'already' : 'done');
    } catch (err) {
      setError(err.response?.data?.detail || 'Something went wrong. Please try again.');
      setState('idle');
    }
  };

  return (
    <div className="fixed inset-0 z-[120] flex items-center justify-center bg-black/70 backdrop-blur-sm px-4"
         data-testid="sell-here-modal">
      <div className="w-full max-w-md rounded-2xl border border-[#ede4ce] bg-[#fffdf8] p-6 shadow-2xl">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-2">
            <Store className="w-5 h-5 text-[#c4622d]" />
            <h2 className="text-lg font-semibold text-[#4a4035]">Sell with us</h2>
          </div>
          <button onClick={close} className="text-[#b0a899] hover:text-[#4a4035] transition-colors"
                  data-testid="sell-here-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        {state === 'done' || state === 'already' ? (
          <div className="mt-6 text-center" data-testid="sell-here-success">
            <CheckCircle2 className="mx-auto w-10 h-10 text-[#5a8f6b]" />
            <p className="mt-3 text-sm text-[#4a4035]">{done}</p>
            {state === 'done' && (
              <p className="mt-2 text-xs text-[#7a6e62]">
                The email has the Vendor Terms, the Marketplace Guidelines and a short
                questionnaire about your work.
              </p>
            )}
            <Button onClick={close} className="mt-5 w-full bg-[#c4622d] hover:bg-[#a8521f]"
                    data-testid="sell-here-done-btn">
              Close
            </Button>
          </div>
        ) : (
          <form onSubmit={submit} className="mt-4 space-y-3">
            <p className="text-sm text-[#7a6e62]">
              Our marketplace is curated rather than open. Tell us a little about your store and
              we&apos;ll send you the agreements to review and a short questionnaire.
            </p>

            <div>
              <label className="text-xs font-medium text-[#4a4035]">Your name</label>
              <Input required value={form.full_name} onChange={set('full_name')}
                     className="mt-1 bg-white text-[#4a4035] placeholder:text-[#b0a899]" data-testid="sell-here-name" />
            </div>
            <div>
              <label className="text-xs font-medium text-[#4a4035]">Business name</label>
              <Input required value={form.business_name} onChange={set('business_name')}
                     className="mt-1 bg-white text-[#4a4035] placeholder:text-[#b0a899]" data-testid="sell-here-business" />
            </div>
            <div>
              <label className="text-xs font-medium text-[#4a4035]">Email address</label>
              <Input required type="email" value={form.email} onChange={set('email')}
                     className="mt-1 bg-white text-[#4a4035] placeholder:text-[#b0a899]" data-testid="sell-here-email" />
            </div>
            <div>
              <label className="text-xs font-medium text-[#4a4035]">
                Anything you&apos;d like us to know <span className="text-[#b0a899]">(optional)</span>
              </label>
              <Textarea rows={3} value={form.note} onChange={set('note')}
                        className="mt-1 bg-white text-[#4a4035] placeholder:text-[#b0a899]" data-testid="sell-here-note" />
            </div>

            {error && (
              <p className="text-xs text-red-600" data-testid="sell-here-error">{error}</p>
            )}

            <Button type="submit" disabled={state === 'sending'}
                    className="w-full bg-[#c4622d] hover:bg-[#a8521f]"
                    data-testid="sell-here-submit">
              {state === 'sending'
                ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending…</>
                : 'Send my details'}
            </Button>
          </form>
        )}
      </div>
    </div>
  );
};

export default SellHereModal;
